import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { format, parseISO } from "date-fns";
import { enUS, es, fr, he, ru } from "date-fns/locale";
import { useClientTranslations } from "../../../lib/i18n/client";
import { getEventTypeColor } from "../../../lib/track/eventTypeColor";
import type { Event } from "../../../types/event";
import styles from "./ComingUpCard.module.css";

const LOCALE_MAP = { en: enUS, es, fr, he, ru } as const;

interface ComingUpCardProps {
	event: Event;
	onClick: (event: Event) => void;
}

/**
 * ComingUpCard (CUP-02, CUP-03).
 *
 * A type-tinted list item for the Coming Up feed. Background uses the
 * `--color-event-{cat}-tint` CSS variable for the event's visual category
 * (D-07 / D-11). The leading info-circle icon uses the matching base color.
 *
 * Date/time is formatted with date-fns using the active i18n locale; the
 * "@ {{date}} · {{time}}" pattern is owned by the `event.dateTime` i18n key
 * (template can vary per locale).
 */
export function ComingUpCard({ event, onClick }: ComingUpCardProps) {
	const { t, locale } = useClientTranslations("track");
	const dateFnsLocale = LOCALE_MAP[locale as keyof typeof LOCALE_MAP] ?? enUS;
	const visualCategory = getEventTypeColor(event.type);
	const typeLabel = t(`eventType.${event.type}`);

	// Pitfall 2: parse for time display, slice for day grouping. Time uses device locale.
	const dateObj = parseISO(event.dateTime);
	const dateTimeFormatted = t("event.dateTime", {
		date: format(dateObj, "EEE, MMM d", { locale: dateFnsLocale }),
		time: format(dateObj, "p", { locale: dateFnsLocale }),
	});

	return (
		<button
			type="button"
			className={`${styles.card} ${styles[`card--${visualCategory}`]}`}
			onClick={() => onClick(event)}
		>
			<div
				className={`${styles.iconCircle} ${styles[`iconCircle--${visualCategory}`]}`}
			>
				<InformationCircleIcon className={styles.icon} />
			</div>
			<div className={styles.body}>
				<span className={styles.typeLabel}>{typeLabel}</span>
				<h4 className={styles.title}>{event.title}</h4>
				<p className={styles.dateTime}>@ {dateTimeFormatted}</p>
			</div>
		</button>
	);
}
